import { useState } from 'react';
import type { Field, GeneratorConfigV1 } from '../../types/generator-config';
import { computePrice, formatPrice } from '../../lib/pricing';
import type { Answers } from '../../lib/visibility';
import { visibleFields, visibleSteps } from '../../lib/visibility';
import { isFieldEmpty } from '../../lib/fieldState';
import { LeadCaptureForm } from './LeadCaptureForm';
import { useSubmitLead, type LeadContact } from '../../hooks/useSubmitLead';

interface SummaryScreenProps {
    config: GeneratorConfigV1;
    answers: Answers;
    onRestart: () => void;
    onEdit: (stepIdx: number) => void;
    /** IDs pour l'enregistrement réel dans Supabase. Absents → mode preview. */
    generatorId?: string;
    organizationId?: string;
}

export function SummaryScreen({
    config,
    answers,
    onRestart,
    onEdit,
    generatorId,
    organizationId,
}: SummaryScreenProps) {
    const price = computePrice(config, answers);
    const steps = visibleSteps(config.steps, answers);
    const leadCapture = config.leadCapture;
    const leadEnabled = !!leadCapture && leadCapture.enabled;
    const previewMode = !generatorId || !organizationId;

    const { submit, submitting, error } = useSubmitLead();
    const [submitted, setSubmitted] = useState(false);

    const handleSubmitLead = async (contact: LeadContact) => {
        if (previewMode) {
            setSubmitted(true);
            return;
        }
        const ok = await submit({
            organizationId: organizationId!,
            generatorId: generatorId!,
            answers,
            contact,
            estimatedTotal: price.total,
        });
        if (ok) setSubmitted(true);
    };

    return (
        <div className="r-summary">
            <div className="r-summary-head">
                <span className="preview-step-eyebrow">Résumé de votre projet</span>
                <h3>Votre estimation</h3>
            </div>

            <div className="r-summary-price">
                <div className="r-summary-price-label">Estimation à partir de</div>
                <div className="r-summary-price-amount">
                    {formatPrice(price.total, price.currency)}
                </div>
                {config.pricing.footnote && (
                    <div className="r-summary-footnote">{config.pricing.footnote}</div>
                )}
                {price.entries.length > 0 && (
                    <details className="r-summary-breakdown">
                        <summary>Détail du calcul</summary>
                        <ul>
                            <li>
                                <span>Base</span>
                                <span>{formatPrice(price.base, price.currency)}</span>
                            </li>
                            {price.entries.map((e, i) => (
                                <li key={i}>
                                    <span>{e.label}</span>
                                    <span className={e.delta >= 0 ? 'is-plus' : 'is-minus'}>
                                        {e.delta >= 0 ? '+' : ''}{formatPrice(e.delta, price.currency)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>

            {!submitted && (
                <div className="r-summary-answers">
                    {steps.map((step, sIdx) => {
                        const fields = visibleFields(step.fields, answers);
                        if (fields.length === 0) return null;
                        return (
                            <div key={step.id} className="r-summary-step">
                                <header>
                                    <h4>{step.title}</h4>
                                    <button
                                        type="button"
                                        className="r-summary-edit"
                                        onClick={() => onEdit(sIdx)}
                                    >
                                        Modifier
                                    </button>
                                </header>
                                <dl>
                                    {fields.map((f) => (
                                        <div key={f.id} className="r-summary-row">
                                            <dt>{f.label}</dt>
                                            <dd>{formatAnswer(f, answers[f.id])}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        );
                    })}
                </div>
            )}

            {leadEnabled && !submitted && (
                <LeadCaptureForm
                    config={leadCapture!}
                    submitting={submitting}
                    error={error}
                    onSubmit={handleSubmitLead}
                    previewMode={previewMode}
                />
            )}

            {leadEnabled && submitted && (
                <div className="lead-success">
                    <div className="lead-success-icon" aria-hidden>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h4>{leadCapture!.successTitle}</h4>
                    <p>{leadCapture!.successMessage}</p>
                    <button type="button" className="preview-btn-ghost" onClick={onRestart}>
                        Faire une nouvelle estimation
                    </button>
                </div>
            )}

            {!leadEnabled && (
                <div className="preview-step-nav">
                    <button type="button" className="preview-btn-ghost" onClick={onRestart}>
                        Recommencer
                    </button>
                </div>
            )}
        </div>
    );
}

function formatAnswer(field: Field, value: unknown): string {
    if (value === null || value === undefined || isFieldEmpty(value as never)) {
        return '—';
    }

    if (Array.isArray(value)) {
        if ('options' in field && Array.isArray((field as { options?: unknown }).options)) {
            const opts = (field as { options: { id: string; label: string }[] }).options;
            const labels = (value as string[]).map((id) =>
                opts.find((o) => o.id === id)?.label ?? id
            );
            return labels.join(', ') || '—';
        }
        return (value as unknown[]).join(', ');
    }

    if (typeof value === 'string' && 'options' in field) {
        const opts = (field as { options: { id: string; label: string }[] }).options;
        const match = opts.find((o) => o.id === value);
        if (match) return match.label;
    }

    if (typeof value === 'number' && (field.type === 'slider' || field.type === 'number')) {
        const unit = (field as { unit?: string }).unit;
        return unit ? `${value} ${unit}` : String(value);
    }

    return String(value);
}
